// Patch 27/29 · mobile bottom bar (floating glass pill): Votar · Finder · Pixie · Eventos · Perfil.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { buzz } from "@/lib/buzz";
import { UNLOCK_LEVEL, levelFromXp } from "@/lib/levels";

const UI = "/pixo/ui";
const HIDE = /^\/(admin|selo)/;

function PixiePop({ onClose }) {
  const { t } = useI18n();
  const H = t.h27;
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[70] sm:hidden" data-testid="pixie-pop">
      <button type="button" aria-label={H.close} className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={H.pixie_hi} className="absolute left-1/2 -translate-x-1/2 w-[min(320px,calc(100vw-32px))] bg-[#141418] border-2 border-amber-400 p-4 tier-pop-soft"
        style={{ bottom: "calc(96px + env(safe-area-inset-bottom))", boxShadow: "0 0 0 2px #000, 0 14px 34px rgba(0,0,0,.6)" }}>
        <div className="font-display text-[24px] uppercase leading-none text-amber-300">{H.pixie_hi}</div>
        <div className="text-[14px] text-stone-300 mt-1.5">{H.pixie_sub}</div>
        <ul className="mt-3 space-y-2">
          {H.pixie_b.map((b, i) => (
            <li key={i} className="flex gap-2.5 text-[14.5px] leading-snug text-stone-100">
              <img src={`${UI}/perk-${i + 1}.png`} alt="" className="pixel w-[18px] h-[18px] mt-0.5 shrink-0" />
              {b}
            </li>
          ))}
        </ul>
        <Link to="/funcionalidades#pixie" onClick={onClose} className="inline-block mt-2.5 text-[12.5px] font-semibold text-amber-300 hover:text-amber-200" data-testid="pixie-pop-more">{t.feat.learn_more} →</Link>
        <div className="flex gap-2 mt-3">
          <Link to="/register" onClick={onClose} className="btn-gold flex-1 text-center font-bold uppercase tracking-[0.1em] text-[13px] py-3" data-testid="pixie-pop-join">{H.pixie_join}</Link>
          <Link to="/login" onClick={onClose} className="w-[96px] text-center border border-white/20 text-stone-100 font-bold uppercase tracking-[0.1em] text-[13px] py-3">{H.pixie_login}</Link>
        </div>
        <span className="absolute left-1/2 -bottom-[9px] w-4 h-4 bg-[#141418] border-r-2 border-b-2 border-amber-400 rotate-45 -translate-x-1/2" />
      </div>
    </div>,
    document.body
  );
}

// patch 29: signed-in Pixie tap opens this menu (the profile already has its own tab)
const PM = [
  { k: "packs", icon: `${UI}/packs.png` },
  { k: "inventory", icon: `${UI}/inventory.png` },
  { k: "shop", icon: "/pixo/shop/store.png" },
  { k: "chest", icon: "/pixo/chest.png" },
  { k: "fish", icon: `${UI}/fish.png` },
  { k: "garden", icon: "/pixo/garden/plants/helios.png" },
];

function PixieMenu({ user, onClose }) {
  const { t } = useI18n();
  const H = t.h29;
  const [note, setNote] = useState(null);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  const base = `/u/${user.username}`;
  const free = !!user.is_admin;   // same rule as the server: only the admin skips the level unlocks
  const lvl = levelFromXp(user.total_xp);
  return createPortal(
    <div className="fixed inset-0 z-[70] sm:hidden" data-testid="pixie-menu">
      <button type="button" aria-label={t.h27.close} className="absolute inset-0 bg-black/55" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={H.pm_title} className="pm-sheet">
        <div className="flex items-center gap-2 mb-3">
          <img src={`${UI}/mascot.png`} alt="" className="pixel w-9 h-auto" />
          <span className="font-display text-[24px] uppercase leading-none text-amber-300">{H.pm_title}</span>
          <Link to={`${base}#pixie`} onClick={onClose} className="ml-auto text-[13px] text-stone-300 underline">{H.pm_profile}</Link>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {PM.map((m) => {
            const need = UNLOCK_LEVEL[m.k];
            const locked = !free && need && lvl < need;
            return locked ? (
              <button key={m.k} type="button" onClick={() => setNote(note === m.k ? null : m.k)} className="pm-item is-locked" aria-expanded={note === m.k} data-testid={`pm-${m.k}`}>
                <img src={m.icon} alt="" />
                <span className="min-w-0 text-left leading-tight">
                  <span className="block">{H.pm[m.k]}</span>
                  <span className="block text-[11.5px] font-semibold text-stone-500">🔒 {H.pm_level.replace("{n}", need)}</span>
                </span>
              </button>
            ) : (
              <Link key={m.k} to={`${base}?open=${m.k}#pixie`} onClick={onClose} className="pm-item" data-testid={`pm-${m.k}`}>
                <img src={m.icon} alt="" />
                {H.pm[m.k]}
              </Link>
            );
          })}
        </div>
        {note && (
          <div className="mt-2.5 text-[13px] text-stone-300 text-center tier-pop-soft" data-testid="pm-locked-note">
            {H.pm_unlock.replace("{n}", UNLOCK_LEVEL[note])} ·{" "}
            <Link to="/funcionalidades#pixie" onClick={onClose} className="font-semibold text-amber-300 hover:text-amber-200">{t.feat.how}</Link>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default function MobileTabBar() {
  const { t } = useI18n();
  const H = t.h27;
  const { user } = useAuth();
  const loc = useLocation();
  const [pop, setPop] = useState(false);
  const [menu, setMenu] = useState(false);
  const hidden = HIDE.test(loc.pathname);
  useEffect(() => {
    document.body.classList.toggle("has-tabbar", !hidden);
    return () => document.body.classList.remove("has-tabbar");
  }, [hidden]);
  useEffect(() => { setPop(false); setMenu(false); }, [loc.pathname, loc.search]);
  if (hidden) return null;

  const p = loc.pathname;
  const me = user ? `/u/${user.username}` : "/login";
  const tabs = [
    { key: "vote", to: "/vote", label: H.tab_vote, on: p.startsWith("/vote") },
    { key: "finder", to: "/finder", label: H.tab_finder, on: p.startsWith("/finder") },
    null,
    { key: "events", to: "/events", label: H.tab_events, on: p.startsWith("/events") },
    { key: "profile", to: me, label: H.tab_profile, on: user ? p === me : p === "/login" || p === "/register" },
  ];
  const pixie = () => {
    buzz();
    return user ? setMenu((v) => !v) : setPop((v) => !v);
  };

  return (
    <>
      <nav className="tabbar sm:hidden" aria-label="tierTCG" data-testid="tabbar">
        <div className="tabbar-grid">
          {tabs.map((tb, i) =>
            tb ? (
              <Link key={tb.key} to={tb.to} className={`tabbar-tab ${tb.on ? "on text-amber-300" : "text-stone-300"}`} aria-current={tb.on ? "page" : undefined} data-testid={`tab-${tb.key}`}>
                <img src={`${UI}/tab-${tb.key}-${tb.on ? "on" : "off"}.png`} alt="" className="tabbar-icon" />
                <span className="text-[12px] mt-0.5 font-semibold leading-none">{tb.label}</span>
              </Link>
            ) : (
              <button key={i} type="button" onClick={pixie} className={`tabbar-tab ${menu || pop ? "on" : ""}`} aria-label="Pixie" aria-expanded={menu || pop} data-testid="tab-pixie">
                <span className="tabbar-halo" />
                <span className="tabbar-shadow" />
                <img src={`${UI}/mascot.png`} alt="" className="tabbar-pixie" />
                <span className="tabbar-plabel">Pixie</span>
              </button>
            )
          )}
        </div>
      </nav>
      {pop && <PixiePop onClose={() => setPop(false)} />}
      {menu && user && <PixieMenu user={user} onClose={() => setMenu(false)} />}
    </>
  );
}
